import { useState } from "react";
import type { Trip, Traveler, TripStop, Objective, BudgetStyle, Pace } from "../../shared/types";
import { OBJECTIVES } from "../../shared/types";
import { Chip, Field, TagInput, Spinner } from "./ui";
import { api } from "../lib/api";
import { getOwnerEmail, setOwnerEmail, rememberTrip } from "../lib/store";

const CURRENCIES = ["USD", "EUR", "GBP", "SGD", "JPY", "AUD", "CAD", "INR", "THB", "IDR"];
const INTEREST_SUGGESTIONS = ["art", "history", "hiking", "beaches", "street food", "museums", "coffee", "live music", "diving", "architecture", "wildlife", "wine", "temples", "markets", "photography"];
const DIET_SUGGESTIONS = ["vegetarian", "vegan", "halal", "kosher", "gluten-free", "nut allergy", "pescatarian", "no pork", "no beef", "dairy-free"];
const ACCESS_SUGGESTIONS = ["step-free access", "low stamina", "wheelchair", "no long walks", "stroller-friendly", "motion sickness"];

const STEPS = ["Basics", "Objectives", "Your group", "Budget", "Preferences", "Review"];

function emptyTraveler(name = ""): Traveler {
  return { name, interests: [], dietary: [], accessibility: [], energyLevel: "moderate", relation: undefined };
}

function newTrip(): Trip {
  return {
    title: "",
    destination: "",
    origin: "",
    startDate: "",
    endDate: "",
    budgetTotal: null,
    budgetCurrency: "USD",
    budgetStyle: "value",
    stops: [],
    objectives: [],
    groupPrefs: { pace: "balanced", mustDo: [], mustAvoid: [], dietaryGroup: [], notes: "" },
    travelers: [{ ...emptyTraveler("Me"), relation: "me" }],
    ownerEmail: getOwnerEmail() || undefined,
  };
}

// Normalise a trip loaded from the API so every field the form touches exists.
function seedTrip(t: Trip): Trip {
  return {
    ...newTrip(),
    ...t,
    origin: t.origin ?? "",
    startDate: t.startDate ?? "",
    endDate: t.endDate ?? "",
    budgetStyle: t.budgetStyle ?? "value",
    stops: t.stops ?? [],
    groupPrefs: {
      pace: t.groupPrefs?.pace ?? "balanced",
      mustDo: t.groupPrefs?.mustDo ?? [],
      mustAvoid: t.groupPrefs?.mustAvoid ?? [],
      dietaryGroup: t.groupPrefs?.dietaryGroup ?? [],
      notes: t.groupPrefs?.notes ?? "",
    },
    travelers: (t.travelers?.length ? t.travelers : newTrip().travelers).map((tr) => ({
      ...emptyTraveler(),
      ...tr,
    })),
  };
}

export function Wizard({
  mode = "create",
  initialTrip,
  slug,
  onComplete,
  onCancel,
}: {
  mode?: "create" | "edit";
  initialTrip?: Trip;
  slug?: string;
  onComplete: (slug: string) => void;
  onCancel?: () => void;
}) {
  const isEdit = mode === "edit";
  const [step, setStep] = useState(0);
  const [trip, setTrip] = useState<Trip>(() => (initialTrip ? seedTrip(initialTrip) : newTrip()));
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const [err, setErr] = useState("");

  const set = (patch: Partial<Trip>) => setTrip((t) => ({ ...t, ...patch }));
  const setPrefs = (patch: Partial<Trip["groupPrefs"]>) =>
    setTrip((t) => ({ ...t, groupPrefs: { ...t.groupPrefs, ...patch } }));

  const setTraveler = (i: number, patch: Partial<Traveler>) =>
    setTrip((t) => ({
      ...t,
      travelers: t.travelers.map((tr, idx) => (idx === i ? { ...tr, ...patch } : tr)),
    }));
  const addTraveler = () => set({ travelers: [...trip.travelers, emptyTraveler()] });
  const removeTraveler = (i: number) =>
    set({ travelers: trip.travelers.filter((_, idx) => idx !== i) });

  const stops = trip.stops ?? [];
  const setStops = (s: TripStop[]) => set({ stops: s });
  const setStop = (i: number, patch: Partial<TripStop>) =>
    setStops(stops.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addStop = () => setStops([...stops, { city: "", nights: null }]);
  const removeStop = (i: number) => setStops(stops.filter((_, idx) => idx !== i));
  const moveStop = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= stops.length) return;
    const next = [...stops];
    [next[i], next[j]] = [next[j], next[i]];
    setStops(next);
  };

  // Nights validation: compare the sum of stop-nights against the date span.
  const tripDays =
    trip.startDate && trip.endDate && Date.parse(trip.endDate) >= Date.parse(trip.startDate)
      ? Math.round((Date.parse(trip.endDate) - Date.parse(trip.startDate)) / 86400000) + 1
      : null;
  const nightsSum = stops.reduce((s, st) => s + (st.nights || 0), 0);
  const nightsWarning =
    tripDays != null && nightsSum > tripDays
      ? `Your stop nights add up to ${nightsSum}, but ${trip.startDate}–${trip.endDate} is only ${tripDays} days. The plan will trim the longest stays to fit — extend your end date or reduce nights.`
      : tripDays != null && nightsSum > 0 && nightsSum < tripDays
      ? `Your stop nights add up to ${nightsSum}, leaving ${tripDays - nightsSum} day${tripDays - nightsSum > 1 ? "s" : ""} unassigned — the extra will go to your last stop.`
      : null;

  const canNext = () => {
    if (step === 0) return trip.destination.trim().length > 0;
    if (step === 2) return trip.travelers.length > 0 && trip.travelers.every((t) => t.name.trim());
    return true;
  };

  async function save(regenerate: boolean) {
    setBusy(true);
    setErr("");
    try {
      if (trip.ownerEmail) setOwnerEmail(trip.ownerEmail);

      if (isEdit && slug) {
        setBusyMsg("Saving your changes…");
        await api.updateTrip(slug, trip);
        rememberTrip({
          slug,
          title: trip.title || `Trip to ${trip.destination}`,
          destination: trip.destination,
          createdAt: trip.createdAt || new Date().toISOString(),
        });
        if (regenerate) {
          setBusyMsg("Rebuilding the itinerary from your updated details…");
          await api.generate(slug).catch(() => {});
        }
        onComplete(slug);
        return;
      }

      setBusyMsg("Creating your trip and generating the first itinerary…");
      const { trip: created } = await api.createTrip(trip);
      rememberTrip({
        slug: created.slug!,
        title: created.title,
        destination: created.destination,
        createdAt: created.createdAt || new Date().toISOString(),
      });
      // Kick off first generation before navigating so the trip page shows a plan.
      await api.generate(created.slug!).catch(() => {});
      onComplete(created.slug!);
    } catch (e: any) {
      setErr(e.message || "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      {/* Stepper */}
      <ol className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-1.5">
            <button
              onClick={() => (isEdit || i < step) && setStep(i)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${
                i === step
                  ? "bg-brand-600 text-white"
                  : i < step
                  ? "bg-brand-50 text-brand-700"
                  : "bg-white text-slate-400 ring-1 ring-slate-200"
              }`}
            >
              <span className="grid h-4 w-4 place-items-center rounded-full text-[10px]">{i + 1}</span>
              {s}
            </button>
            {i < STEPS.length - 1 && <span className="text-slate-300">›</span>}
          </li>
        ))}
      </ol>

      <div className="card min-h-[22rem]">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Where and when?</h2>
            <Field label="Destination / region *" hint="The overall place, e.g. Yunnan, China">
              <input className="input" value={trip.destination} placeholder="e.g. Lisbon, Portugal"
                onChange={(e) => set({ destination: e.target.value })} />
            </Field>

            {/* Multi-city route builder */}
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Cities / route <span className="font-normal text-slate-400">(optional)</span></p>
                  <p className="text-xs text-slate-400">Add stops in order for a multi-city trip. Leave empty for a single base.</p>
                </div>
                <button className="btn-soft" onClick={addStop}>+ Add city</button>
              </div>
              {stops.length > 0 && (
                <div className="mt-3 space-y-2">
                  {stops.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">{i + 1}</span>
                      <input className="input flex-1" value={s.city} placeholder={`City ${i + 1}, e.g. ${["Kunming", "Dali", "Lijiang"][i] || "city"}`}
                        onChange={(e) => setStop(i, { city: e.target.value })} />
                      <div className="flex items-center gap-1">
                        <input type="number" min={0} max={30} className="input w-16 text-center" value={s.nights ?? ""} placeholder="nts"
                          onChange={(e) => setStop(i, { nights: e.target.value ? Number(e.target.value) : null })} title="Nights here" />
                      </div>
                      <div className="flex flex-col">
                        <button className="text-slate-300 hover:text-brand-600 disabled:opacity-30" disabled={i === 0} onClick={() => moveStop(i, -1)} title="Move up">▲</button>
                        <button className="text-slate-300 hover:text-brand-600 disabled:opacity-30" disabled={i === stops.length - 1} onClick={() => moveStop(i, 1)} title="Move down">▼</button>
                      </div>
                      <button className="text-slate-400 hover:text-red-500" onClick={() => removeStop(i)} title="Remove">✕</button>
                    </div>
                  ))}
                  <p className="pt-1 text-xs text-slate-400">
                    Route: {stops.filter((s) => s.city.trim()).map((s) => s.city).join(" → ") || "—"}
                    {stops.some((s) => s.nights) ? "" : " · nights optional — days are split evenly if blank"}
                  </p>
                  {nightsWarning && (
                    <p
                      className={`rounded-lg px-3 py-2 text-xs font-medium ${
                        nightsSum > (tripDays ?? Infinity)
                          ? "bg-amber-50 text-amber-700"
                          : "bg-brand-50 text-brand-600"
                      }`}
                    >
                      {nightsSum > (tripDays ?? Infinity) ? "⚠ " : "ℹ "}
                      {nightsWarning}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Departing from">
                <input className="input" value={trip.origin} placeholder="e.g. London"
                  onChange={(e) => set({ origin: e.target.value })} />
              </Field>
              <Field label="Trip name">
                <input className="input" value={trip.title} placeholder="(optional)"
                  onChange={(e) => set({ title: e.target.value })} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Start date">
                <input type="date" className="input" value={trip.startDate}
                  onChange={(e) => set({ startDate: e.target.value })} />
              </Field>
              <Field label="End date">
                <input type="date" className="input" value={trip.endDate} min={trip.startDate}
                  onChange={(e) => set({ endDate: e.target.value })} />
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">What's this trip about?</h2>
            <p className="text-sm text-slate-500">Pick the objectives that matter to the group. The itinerary is built around these.</p>
            <div className="flex flex-wrap gap-2">
              {OBJECTIVES.map((o) => (
                <Chip key={o} label={o} active={trip.objectives.includes(o)}
                  onClick={() =>
                    set({
                      objectives: trip.objectives.includes(o)
                        ? trip.objectives.filter((x) => x !== o)
                        : [...trip.objectives, o as Objective],
                    })
                  } />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Who's going?</h2>
              <button className="btn-soft" onClick={addTraveler}>+ Add traveler</button>
            </div>
            <p className="text-sm text-slate-500">Individual demographics and preferences — the plan tries to satisfy everyone.</p>
            <div className="space-y-3">
              {trip.travelers.map((t, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
                  <div className="mb-3 flex items-center gap-2">
                    <input className="input flex-1" value={t.name} placeholder="Name"
                      onChange={(e) => setTraveler(i, { name: e.target.value })} />
                    {trip.travelers.length > 1 && (
                      <button className="text-slate-400 hover:text-red-500" onClick={() => removeTraveler(i)} title="Remove">✕</button>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Relation">
                      <select className="input" value={t.relation || ""} onChange={(e) => setTraveler(i, { relation: e.target.value as any })}>
                        <option value="">—</option>
                        {["me", "partner", "family", "friend", "colleague"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </Field>
                    <Field label="Age band">
                      <select className="input" value={t.ageBand || ""} onChange={(e) => setTraveler(i, { ageBand: e.target.value as any })}>
                        <option value="">—</option>
                        {["infant", "child", "teen", "adult", "senior"].map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </Field>
                    <Field label="Energy">
                      <select className="input" value={t.energyLevel || "moderate"} onChange={(e) => setTraveler(i, { energyLevel: e.target.value as any })}>
                        {["low", "moderate", "high"].map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-3 space-y-3">
                    <Field label="Interests"><TagInput values={t.interests} onChange={(v) => setTraveler(i, { interests: v })} placeholder="Type an interest, Enter" suggestions={INTEREST_SUGGESTIONS} /></Field>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Dietary"><TagInput values={t.dietary} onChange={(v) => setTraveler(i, { dietary: v })} placeholder="Any restrictions" suggestions={DIET_SUGGESTIONS} /></Field>
                      <Field label="Accessibility / mobility"><TagInput values={t.accessibility} onChange={(v) => setTraveler(i, { accessibility: v })} placeholder="Any needs" suggestions={ACCESS_SUGGESTIONS} /></Field>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Budget</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Total budget" hint="For the whole trip, whole party">
                <input type="number" min={0} className="input" value={trip.budgetTotal ?? ""} placeholder="e.g. 3000"
                  onChange={(e) => set({ budgetTotal: e.target.value ? Number(e.target.value) : null })} />
              </Field>
              <Field label="Currency">
                <select className="input" value={trip.budgetCurrency} onChange={(e) => set({ budgetCurrency: e.target.value })}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Style">
                <select className="input" value={trip.budgetStyle} onChange={(e) => set({ budgetStyle: e.target.value as BudgetStyle })}>
                  {(["shoestring", "value", "comfort", "luxury"] as BudgetStyle[]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <div className="rounded-xl bg-brand-50 p-4 text-sm text-brand-700">
              The itinerary estimates costs against this and flags if the plan runs over.
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Group preferences</h2>
            <Field label="Pace">
              <div className="flex gap-2">
                {(["relaxed", "balanced", "packed"] as Pace[]).map((p) => (
                  <Chip key={p} label={p} active={trip.groupPrefs.pace === p} onClick={() => setPrefs({ pace: p })} />
                ))}
              </div>
            </Field>
            <Field label="Must-do (as a group)"><TagInput values={trip.groupPrefs.mustDo} onChange={(v) => setPrefs({ mustDo: v })} placeholder="e.g. see the old town" /></Field>
            <Field label="Must-avoid"><TagInput values={trip.groupPrefs.mustAvoid} onChange={(v) => setPrefs({ mustAvoid: v })} placeholder="e.g. long bus rides" /></Field>
            <Field label="Whole-party dietary constraints"><TagInput values={trip.groupPrefs.dietaryGroup} onChange={(v) => setPrefs({ dietaryGroup: v })} placeholder="Applies to everyone" suggestions={DIET_SUGGESTIONS} /></Field>
            <Field label="Anything else?">
              <textarea className="input min-h-[5rem]" value={trip.groupPrefs.notes} placeholder="Free text for the planner"
                onChange={(e) => setPrefs({ notes: e.target.value })} />
            </Field>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">{isEdit ? "Review & save" : "Review & create"}</h2>
            <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
              <Row k="Destination" v={trip.destination} />
              {stops.filter((s) => s.city.trim()).length > 0 && (
                <Row k="Route" v={stops.filter((s) => s.city.trim()).map((s) => `${s.city}${s.nights ? ` (${s.nights}n)` : ""}`).join(" → ")} />
              )}
              <Row k="From" v={trip.origin || "—"} />
              <Row k="Dates" v={trip.startDate ? `${trip.startDate} → ${trip.endDate || "?"}` : "flexible"} />
              <Row k="Objectives" v={trip.objectives.join(", ") || "general"} />
              <Row k="Travelers" v={trip.travelers.map((t) => t.name).join(", ")} />
              <Row k="Budget" v={trip.budgetTotal ? `${trip.budgetTotal} ${trip.budgetCurrency} · ${trip.budgetStyle}` : trip.budgetStyle || "—"} />
              <Row k="Pace" v={trip.groupPrefs.pace} />
            </div>
            <Field label="Your email (optional)" hint="Only used to group your trips and build insights over time. No login, no spam.">
              <input type="email" className="input" value={trip.ownerEmail || ""} placeholder="you@example.com"
                onChange={(e) => set({ ownerEmail: e.target.value })} />
            </Field>
            {nightsWarning && nightsSum > (tripDays ?? Infinity) && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">⚠ {nightsWarning}</p>
            )}
            {isEdit && (
              <p className="text-xs text-slate-400">
                Saving updates your saved trip. Choose <b>Save &amp; regenerate</b> to rebuild the
                itinerary from these details, or <b>Save only</b> to keep the current plan.
              </p>
            )}
            {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
            {busy && <div className="rounded-lg bg-brand-50 p-4"><Spinner label={busyMsg || "Working…"} /></div>}
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={step === 0 || busy} onClick={() => setStep((s) => Math.max(0, s - 1))}>← Back</button>
          {isEdit && onCancel && (
            <button className="btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
          )}
        </div>
        {step < STEPS.length - 1 ? (
          <button className="btn-primary" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}>Next →</button>
        ) : isEdit ? (
          <div className="flex gap-2">
            <button className="btn-ghost" disabled={busy} onClick={() => save(false)}>Save only</button>
            <button className="btn-primary" disabled={busy} onClick={() => save(true)}>💾 Save &amp; regenerate</button>
          </div>
        ) : (
          <button className="btn-primary" disabled={busy} onClick={() => save(true)}>✨ Create &amp; generate itinerary</button>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-24 shrink-0 font-medium text-slate-400">{k}</span>
      <span className="text-slate-700">{v}</span>
    </div>
  );
}
